import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { RootState } from '../index';

export type LimitItem = {
    limit: number;
    current: number;
};

export type PlayerLimits = {
    [key: string]: LimitItem | undefined;
};

interface PlayerLimitsState {
    data: PlayerLimits | null;
    loading: boolean;
    error: string | null;
    lastFetched: string | null;
}

const initialState: PlayerLimitsState = {
    data: null,
    loading: false,
    error: null,
    lastFetched: null
};

const apiUrl = window.config.playerApiUrl;

export const fetchPlayerLimits = createAsyncThunk(
    'playerLimits/fetchPlayerLimits',
    async (_, { rejectWithValue, getState }) => {
        try {
            const state: RootState = getState() as RootState;

            if (state.authentication.access_token && state.authentication.auth_type === 'user') {
                const response = await axios.get<PlayerLimits>(`${apiUrl}/player/data/player-limits`, {
                    headers: {
                        Authorization: 'Bearer ' + state.authentication.access_token,
                    },
                });

                if (response.data) {
                    return { data: response.data, success: true };
                }
            }
            return rejectWithValue({
                error: 'Failed to fetch player limits',
            });
        } catch (err: any) {
            const errResp = { error: err.toString() };
            return rejectWithValue(errResp);
        }
    }
);

const playerLimitsSlice = createSlice({
    name: 'playerLimits',
    initialState,
    reducers: {
        updatePlayerLimit: (state, action: PayloadAction<{ limitType: string; limit: number; }>) => {
            const { limitType, limit } = action.payload;

            if (state.data) {
                if (state.data[limitType]) {
                    state.data[limitType]!.limit = limit;
                } else {
                    state.data[limitType] = {
                        limit: limit,
                        current: 0
                    };
                }
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPlayerLimits.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchPlayerLimits.fulfilled, (state, action: PayloadAction<{ data: PlayerLimits; success: boolean; }>) => {
                state.loading = false;
                state.data = action.payload.data;
                state.lastFetched = new Date().toISOString();
            })
            .addCase(fetchPlayerLimits.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string || 'Unknown error occurred';
            });
    }
});

// Export actions
export const { updatePlayerLimit } = playerLimitsSlice.actions;

// Export selectors
export const selectPlayerLimits = (state: { playerLimits: PlayerLimitsState; }) => state.playerLimits.data;
export const selectPlayerLimitsLoading = (state: { playerLimits: PlayerLimitsState; }) => state.playerLimits.loading;
export const selectPlayerLimitsError = (state: { playerLimits: PlayerLimitsState; }) => state.playerLimits.error;
export const selectPlayerLimitsLastFetched = (state: { playerLimits: PlayerLimitsState; }) => state.playerLimits.lastFetched;

// Helper selector to get a specific limit by key
export const selectLimitByKey = (limitKey: string) => (state: { playerLimits: PlayerLimitsState; }) => {
    const data = state.playerLimits.data;
    if (!data) return undefined;

    return data[limitKey];
};

export default playerLimitsSlice.reducer;
